'use client';

import { useParams } from 'next/navigation';
import { useMemo } from 'react';
import { LISTING_PREFIX, isListingSegment } from '@/lib/listing-url';
import { useAiFoodOrderStore } from '@/store';

/**
 * Slug city THẬT trên URL, hoặc `undefined` khi đang ở chế độ rootless.
 *
 * Không đọc thẳng `useParams().city`: URL listing rootless
 * (`/danh-sach-cua-hang-co-freeship`) do route `[city]` phục vụ nên `params.city`
 * trả về chính segment listing. Xem {@link isListingSegment}.
 */
export function useUrlCitySlug(): string | undefined {
  const params = useParams();
  const raw = Array.isArray(params.city) ? params.city[0] : params.city;

  if (!raw || typeof raw !== 'string') return undefined;
  return isListingSegment(raw) ? undefined : raw;
}

/**
 * Sinh URL điều hướng theo luật: **slug chỉ tồn tại khi nó KHÁC city của bạn,
 * hoặc bạn chưa có địa chỉ.**
 *
 * Nói cách khác có hai chế độ, phân biệt bằng URL hiện tại chứ không phải state:
 *
 * - **Chế độ địa chỉ** (URL không có segment city — `/`, `/solomeal`,
 *   `/danh-sach-cua-hang-*`): mọi link sinh ra đều rootless. Đây là chế độ mặc
 *   định sau khi user đã nhập địa chỉ.
 * - **Chế độ duyệt city** (URL có segment city — `/quang-nam/...`): giữ nguyên
 *   slug đó cho mọi link, để user còn ở lại city họ đang xem.
 *
 * Lấy chế độ từ URL là chủ ý: state (`city_slug` trong store) rehydrate không
 * đồng bộ với render, dùng nó sẽ có nhịp link nhảy sai ngay sau khi tải trang.
 * Nó cũng là lý do TUYỆT ĐỐI không ghép tay `/${citySlug}/...`: ở rootless,
 * `citySlug` fallback về city của địa chỉ nên link ra `/da-nang/...`, user bấm
 * xong bị server chuẩn hoá ngược về rootless — thấy rõ hai nhịp chuyển trang.
 */
export function useCityNavigation() {
  const storeCitySlug = useAiFoodOrderStore.use.city_slug();
  const urlCitySlug = useUrlCitySlug();

  // Prefix rỗng = rootless. Chỉ có prefix khi URL đang mang segment city.
  const prefix = urlCitySlug ? `/${urlCitySlug}` : '';

  // Slug "hiệu lực" cho các callsite cần tên city (không phải để ghép URL).
  const citySlug = urlCitySlug ?? storeCitySlug ?? 'da-nang';

  const routes = useMemo(
    () => ({
      // Trang chủ
      home: prefix || '/',
      solomeal: `${prefix}/solomeal`,

      // Danh sách cửa hàng (search)
      search: `${prefix}/${LISTING_PREFIX}`,
      solomealSearch: `${prefix}/${LISTING_PREFIX}-solomeal`,

      /**
       * Danh sách quán kèm bộ lọc. Bộ lọc nằm ngay trong segment
       * (`danh-sach-cua-hang-tra-sua-co-freeship`) chứ không phải query string.
       */
      listing: (suffix?: string) =>
        `${prefix}/${LISTING_PREFIX}${suffix ? `-${suffix}` : ''}`,

      // Chi tiết cửa hàng - Route mới với city_slug
      storeDetail: (storeSlug: string) => `/cua-hang/${storeSlug}`,

      // Order routes cho luồng chính (desktop/mobile web)
      orderStore: (storeId: string) => `/order/${storeId}`,
      orderCart: (storeId: string) => `/order/${storeId}/cart`,

      // Order routes cho webview (luồng /m)
      orderStore_wv: (storeId: string) => `/m/order-wv/${storeId}`,
      orderCart_wv: (storeId: string) => `/m/order-wv/${storeId}/cart`,

      // User routes
      user: '/user',
      orderList: '/don-hang',

      // Static routes
      sentryExamplePage: '/sentry-example-page',
      order: '/order',
      store: '/store',
    }),
    [prefix]
  );

  return {
    citySlug,
    routes,
  };
}

/** Bộ URL đã gắn đúng chế độ (rootless hay có slug city). */
export type CityRoutes = ReturnType<typeof useCityNavigation>['routes'];

/**
 * Hook đơn giản chỉ để lấy city routes
 * Dùng khi chỉ cần access routes mà không cần logic phức tạp
 */
export function useCityRoutes(): CityRoutes {
  const { routes } = useCityNavigation();
  return routes;
}
